import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { listRequests } from '@/services/api'
import { StatusBadge } from '@/components/StatusBadge'
import { Button } from '@/components/ui/button'

export default function AdminDashboard() {
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Don't fire queries while processing OAuth callback (token in URL)
  const hasOAuthToken = !!searchParams.get('token')
  const [authReady, setAuthReady] = useState(
    () => !hasOAuthToken && !!localStorage.getItem('auth_token')
  )

  // Extract token from URL (OAuth callback) and store in localStorage
  useEffect(() => {
    const token = searchParams.get('token')
    if (token) {
      localStorage.setItem('auth_token', token)
      setAuthReady(true)
      // Remove token from URL for security
      navigate('/ops-portal', { replace: true })
    }
  }, [searchParams, navigate])

  const handleLogout = () => {
    localStorage.removeItem('auth_token')
    navigate('/ops-portal/login')
  }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-requests', statusFilter],
    queryFn: () => listRequests({ status: statusFilter }),
    enabled: authReady,
    retry: 2,
  })

  const statuses = [
    { key: 'pending', label: 'En attente' },
    { key: 'approved', label: 'Approuvé' },
    { key: 'running', label: 'En cours' },
    { key: 'completed', label: 'Terminé' },
    { key: 'failed', label: 'Échoué' },
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-bronze shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">Tableau de Bord</h1>
            <p className="text-white/70 text-sm">Portail d'Administration AIP</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/ops-portal/qualification-candidates">
              <Button
                variant="outline"
                className="border-white text-white bg-transparent hover:bg-white/20 hover:text-white"
              >
                Candidats programme de qualification
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-white text-white bg-transparent hover:bg-white/20 hover:text-white"
            >
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Filters */}
        <div className="mb-6">
          <h2 className="text-sm font-medium text-muted-foreground mb-3">Filtrer par statut</h2>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={statusFilter === undefined ? 'default' : 'outline'}
              onClick={() => setStatusFilter(undefined)}
              className={statusFilter === undefined ? 'bg-gold hover:bg-gold/90' : 'border-border'}
            >
              Tous
            </Button>
            {statuses.map((status) => (
              <Button
                key={status.key}
                variant={statusFilter === status.key ? 'default' : 'outline'}
                onClick={() => setStatusFilter(status.key)}
                className={statusFilter === status.key ? 'bg-gold hover:bg-gold/90' : 'border-border'}
              >
                {status.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-border overflow-hidden">
          <div className="bg-secondary/50 px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-bronze">Demandes d'Onboarding</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-secondary/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-border">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gold" />
                        <span className="text-muted-foreground">Chargement...</span>
                      </div>
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center">
                      <p className="text-destructive mb-2">Erreur lors du chargement des demandes</p>
                      <Button variant="outline" size="sm" onClick={() => refetch()}>
                        Réessayer
                      </Button>
                    </td>
                  </tr>
                ) : !data?.items?.length ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                      Aucune demande trouvée
                    </td>
                  </tr>
                ) : (
                  data?.items?.map((request: any) => (
                    <tr key={request.id} className="hover:bg-secondary/20 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-foreground">{request.client_name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-muted-foreground">
                        {request.contact_email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={request.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-muted-foreground">
                        {new Date(request.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/ops-portal/requests/${request.id}`}>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-bronze text-bronze hover:bg-bronze hover:text-white"
                          >
                            Voir
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
